import {Component, Input, OnInit} from '@angular/core';
import {UserSubscription, UserSubscriptionType} from "../../../../../../user/user-subscription";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../../store";
import {Observable} from "rxjs";
import {IBoosterBenchmark} from "../../../../../../store/reducers/booster.reducer";
import {filter, first, takeUntil} from "rxjs/operators";
import {TypeUtils} from "../../../../../../shared/utils/type-utils";
import {
  GlobalEventDispatcherService
} from "../../../../../../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {GoogleAnalyticsService} from "../../../../../../shared/services/google-analytics/google-analytics.service";
import {DbModalService} from "../../../../../../modules/modal/modal";
import {ActivatedRoute, Router} from "@angular/router";
import {BoosterMode} from "../../../services/booster.service";
import {TextUtils} from "../../../../../../shared/utils/text-utils";
import {BoosterPage} from "../../../mappers/booster-page";
import {Subject} from "rxjs";
import {BuilderSectionsInteractionService} from "../../../../../../builder-v2/builder-sections-interaction.service";

@Component({
    selector: 'db-booster-optimization-status',
    templateUrl: './booster-optimization-status.component.html',
    styleUrls: ['./booster-optimization-status.component.scss'],
    standalone: false
})
export class BoosterOptimizationStatusComponent implements OnInit {

  @Input() adminActivateBoosterUrl: string;
  @Input() websiteId: number;
  @Input() websiteUrl: string;
  @Input() showIncognitoMode?: boolean;
  @Input() websiteCDNEnabled?: boolean;
  @Input() userSubscription?: UserSubscription;
  @Input() userSubscriptionType: UserSubscription['type'];
  @Input() isHosted: boolean;
  @Input() cacheEnabled: boolean;
  @Input() cachingStatus: boolean;
  @Input() maxOptimizedPagesLength: number;
  @Input() frontEndMenu?: boolean = false;
  @Input() testMode: 'on'|'off'|null;
  @Input() testModeEnabled: boolean;
  @Input() boosterMode: BoosterMode;
  @Input() testModeLoading: boolean = true;
  @Input() forHosting: boolean;
  @Input() cdnIsActive: boolean;
  @Input() websiteIsStaging: boolean;
  @Input() canEnable: boolean;
  public showTestMode: boolean = false;
  public pageCount: string;
  public toggleKnowledgeBase: boolean;

  public readonly UserSubscriptionType = UserSubscriptionType;

  public optimizedPages$: Observable<any>;
  public optimizedPages: BoosterPage[] | null = [];
  public benchmark$: Observable<IBoosterBenchmark>;
  public options: any;

  public pagesCount: number;
  public notOptimizedPagesCount: number;
  public pageCountLoading: boolean = true;
  public boosterPluginIsActive: boolean = false;
  public boosterPluginIsOld: boolean = false;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  public companyName: string = 'Dashboard';

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _store: Store<fromStore.DbStoreState>,
    private _modalService: DbModalService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _globalEventDispatcher: GlobalEventDispatcherService,
    private _builderSectionsInteractionService: BuilderSectionsInteractionService
  ) {}

  ngOnInit() {
    this._getOptions();
    this._getPages();
    this.optimizedPages$ = this._store.select(fromStore.getWebsiteBoosterPages(this.websiteId));
    this.benchmark$ = this._store.select(fromStore.getWebsiteBoosterBenchmark(this.websiteId));
    this._getTestMode();
    this._checkBoosterState();
    this._getCompanyInfo();
  }

    private _getCompanyInfo(): void {
        this._builderSectionsInteractionService.companyInfo
            .pipe(
                filter(companyInfo => !!companyInfo),
                takeUntil(this._destroyStream$),
                first()
            )
            .subscribe(companyInfo => {
                this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
            });
    }

  private _checkBoosterState(): void {
    this._store.select(fromStore.getWebsiteOptimizerPlugin(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((optimizerPlugin): void => {
        this.boosterPluginIsOld = TypeUtils.isEmpty(optimizerPlugin);
        if (optimizerPlugin && !TypeUtils.isEmpty(optimizerPlugin)) {
          this.boosterPluginIsActive = optimizerPlugin.active;
        }
      });
  }

  private _getTestMode(): void {
    this._store.select(fromStore.getWebsiteBoosterTestMode(this.websiteId))
      .pipe(
        filter(mode => !!mode)
      )
      .subscribe(mode => {
        if (!TypeUtils.isNull(mode)) {
          this.testMode = TypeUtils.isDefined(mode) && (mode === 'on' || mode === 'off') ? mode : null;
          this.showTestMode = !TypeUtils.isNull(this.testMode);
        }
      });
  }

  public toggleKnowLedgeBase() {
    this.toggleKnowledgeBase = !this.toggleKnowledgeBase;
    this._store.dispatch(new fromStore.ToggleKnowledgeBase(this.toggleKnowledgeBase ? {articleId: 6216454280338, scrollToEl: ''} : false));
  }

  private _getPages(): void {
    this._store.select(fromStore.getWebsiteBoosterPages(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(pages => {
          this.optimizedPages = pages;
          this._getPagesCount();
      });
  }

  public _getPagesCount(): void {
    this._store.select(fromStore.getWebsiteBoosterPagesCount(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(pagesCount => {
          this.pagesCount = pagesCount;
          this.notOptimizedPagesCount = this.optimizedPages && this.pagesCount > this.optimizedPages.length ? this.pagesCount - this.optimizedPages.length : 0;
          this.options[3].text = this.pagesCount > 0 ? this.pagesCount === 1 ? "1 fully optimized page of your site"
              : TextUtils.getText('text_proOptimCdnOption4', {pages: this.pagesCount})
            : TextUtils.getText('text_proOptimCdnOption4NoWebsite');
          this.pageCountLoading = false;
      });
  }

  private _getOptions(): void {
    this.options = [
      {
        img: "assets/images/icons/speed-green.svg",
        text: TextUtils.getText('text_proOptimCdnOption1'),
      },
      {
        img: "assets/images/icons/speed-time.svg",
        text: TextUtils.getText('text_proOptimCdnOption2'),
      },
      {
        img: "assets/images/icons/speed-globe.svg",
        text: TextUtils.getText('text_proOptimCdnOption3'),
      },
      {
        img: "assets/images/icons/speed-pages.svg",
        text: this.pagesCount > 0 ? this.pagesCount === 1 ? "1 fully optimized page of your site"
            : TextUtils.getText('text_proOptimCdnOption4', {pages: this.pagesCount})
          : TextUtils.getText('text_proOptimCdnOption4NoWebsite')
      },
    ];
  }

  /*public changeTab(tab: string) {
    this._sendEvent('Settings tab button click, from Test Mode notice');
    this._globalEventDispatcher.dispatchEvent('activeTab', 'settings');
    sessionStorage.removeItem('activeTab');
    sessionStorage.setItem("activeTab", tab);
  }*/

  private _sendEvent(action) {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: action,
      eventLabel: 'Speed Booster',
      websiteId: this.websiteId
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  public redirectToBoosterPro(): void {
    this.sendEvent('Status bar');
    this._router.navigate(['/upgrade-plan']);
  }

  public sendEvent(label: string): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: 'Upgrade button click',
      eventLabel: 'Speed Booster: ' + label,
      websiteId: this.websiteId
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  public upgrade() {
    if (!!this.isHosted) {
      this.sendEvent('buy now from main page');
      this._router.navigate([`../cloudflare`], {relativeTo: this._route});
    }
    else {
      this.sendEvent('upgrade from main page');
      this._router.navigateByUrl('/upgrade-plan');
    }
  }
}
